import { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowLeft, Building2, Search, CheckCircle } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';

export default function ClaimGymScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;

  const [query, setQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedGym, setSelectedGym] = useState<any>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const cityFilterRef = useRef('');
  useEffect(() => { cityFilterRef.current = cityFilter; }, [cityFilter]);

  const userId = session?.user?.id ?? '';

  const handleSearch = async (text: string) => {
    setQuery(text);
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const words = text.trim().split(/\s+/);
      const nameQuery = words.length > 1 ? words.slice(0, -1).join(' ') : text.trim();
      const possibleCity = words.length > 1 ? words[words.length - 1] : '';

      let dbQuery = supabase
        .from('gyms')
        .select('id, name, city, address')
        .ilike('name', `%${nameQuery}%`)
        .limit(10);

      // Apply city filter from dedicated field OR from last word of search
      const cityToFilter = cityFilterRef.current.trim().length > 1 ? cityFilterRef.current.trim() : possibleCity;
      if (cityToFilter.length > 1) {
        dbQuery = dbQuery.ilike('city', `%${cityToFilter}%`);
      }

      const { data } = await dbQuery;
      setResults(data ?? []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const handleClaim = async () => {
    if (!selectedGym || !userId) return;
    setClaiming(true);
    try {
      const { error } = await supabase.from('gym_memberships').upsert(
        {
          gym_id: selectedGym.id,
          user_id: userId,
          role: 'owner',
          status: 'pending',
        },
        { onConflict: 'gym_id,user_id' }
      );
      if (error) throw error;
      setClaimed(true);
    } catch (err: any) {
      Alert.alert('Error', err?.message ?? 'Something went wrong. Please try again.');
    }
    setClaiming(false);
  };

  if (claimed) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
          <CheckCircle size={48} color={c.accent} />
          <Text
            style={{
              fontSize: 22,
              fontFamily: TYPO.heading.bold,
              color: c.text,
              marginTop: 16,
              textAlign: 'center',
            }}>
            Claim Submitted
          </Text>
          <Text
            style={{
              fontSize: 14,
              color: c.textSecondary,
              textAlign: 'center',
              marginTop: 8,
              lineHeight: 22,
            }}>
            We'll review your claim for {selectedGym?.name} and reach out within 48 hours.
          </Text>

          {/* Club trial CTA */}
          <View style={{ backgroundColor: c.surface, borderWidth: 1, borderColor: c.accent, borderRadius: 4, padding: 16, marginTop: 24, width: '100%' }}>
            <Text style={{ fontSize: 11, color: c.accent, fontFamily: 'Inter_700Bold', letterSpacing: 1.5, marginBottom: 8 }}>
              WHILE YOU WAIT
            </Text>
            <Text style={{ color: c.text, fontSize: 15, fontFamily: 'Inter_700Bold', marginBottom: 4 }}>
              Start Your Club Trial
            </Text>
            <Text style={{ color: c.textSecondary, fontSize: 13, lineHeight: 20, marginBottom: 12 }}>
              Get instant access to gym leaderboards, TV Mode, member management, and team challenges. First 7 days free.
            </Text>
            <TouchableOpacity
              onPress={() => router.push('/club-onboarding' as any)}
              style={{ backgroundColor: c.accent, borderRadius: 4, padding: 12, alignItems: 'center' }}
            >
              <Text style={{ color: c.accentForeground, fontFamily: 'Inter_700Bold', fontSize: 15 }}>
                See Club Plans →
              </Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity onPress={() => router.back()} style={{ marginTop: 16, padding: 12 }}>
            <Text style={{ color: c.textTertiary, fontSize: 14 }}>Skip for now</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Header */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: 16,
          gap: 12,
          borderBottomWidth: 1,
          borderBottomColor: c.border,
        }}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={8}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontFamily: TYPO.heading.bold, color: c.text }}>
          Claim Your Gym
        </Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        <Text style={{ color: c.textSecondary, fontSize: 14, lineHeight: 22, marginBottom: 20 }}>
          Are you a gym owner or manager? Claim your gym to unlock leaderboards, member management,
          TV Mode, and more.
        </Text>

        {/* City filter */}
        <TextInput
          style={{
            backgroundColor: c.surface,
            borderWidth: 1,
            borderColor: c.border,
            borderRadius: 4,
            paddingHorizontal: 12,
            paddingVertical: 12,
            color: c.text,
            fontSize: 15,
            marginBottom: 8,
          }}
          placeholder="Filter by city (optional)..."
          placeholderTextColor={c.textTertiary}
          value={cityFilter}
          onChangeText={(t) => { setCityFilter(t); if (query.length > 1) handleSearch(query); }}
          autoCapitalize="words"
        />

        {/* Search */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: c.surface,
            borderWidth: 1,
            borderColor: c.border,
            borderRadius: 4,
            paddingHorizontal: 12,
            marginBottom: 12,
          }}>
          <Search size={16} color={c.textTertiary} />
          <TextInput
            style={{
              flex: 1,
              color: c.text,
              fontSize: 15,
              paddingVertical: 12,
              marginLeft: 8,
            }}
            placeholder="Search for your gym..."
            placeholderTextColor={c.textTertiary}
            value={query}
            onChangeText={handleSearch}
            autoCapitalize="words"
          />
          {searching && <ActivityIndicator size="small" color={c.accent} />}
        </View>

        {/* Results */}
        {results.map((gym) => (
          <TouchableOpacity
            key={gym.id}
            onPress={() => {
              setSelectedGym(gym);
              setResults([]);
              setQuery(gym.name);
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              backgroundColor: selectedGym?.id === gym.id ? c.accent + '15' : c.surface,
              borderWidth: 1,
              borderColor: selectedGym?.id === gym.id ? c.accent : c.border,
              borderRadius: 4,
              padding: 14,
              marginBottom: 8,
            }}>
            <Building2 size={18} color={c.accent} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: c.text, fontSize: 14, fontFamily: TYPO.body.semibold }}>
                {gym.name}
              </Text>
              {gym.city && (
                <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2 }}>
                  {gym.city}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        ))}

        {/* Selected gym confirm */}
        {selectedGym && (
          <View
            style={{
              backgroundColor: c.surface,
              borderWidth: 1,
              borderColor: c.accent,
              borderRadius: 4,
              padding: 16,
              marginTop: 16,
            }}>
            <Text
              style={{
                fontSize: 11,
                color: c.accent,
                fontFamily: TYPO.heading.bold,
                letterSpacing: 1.5,
                marginBottom: 8,
              }}>
              SELECTED GYM
            </Text>
            <Text style={{ color: c.text, fontSize: 16, fontFamily: TYPO.heading.bold }}>
              {selectedGym.name}
            </Text>
            {selectedGym.city && (
              <Text style={{ color: c.textSecondary, fontSize: 13, marginTop: 2 }}>
                {selectedGym.city}
              </Text>
            )}

            <Text
              style={{ color: c.textSecondary, fontSize: 13, lineHeight: 20, marginTop: 12 }}>
              By submitting, you confirm you are an authorized representative of this gym. We'll
              verify your claim within 48 hours.
            </Text>

            <TouchableOpacity
              onPress={handleClaim}
              disabled={claiming}
              style={{
                backgroundColor: c.accent,
                borderRadius: 4,
                padding: 14,
                alignItems: 'center',
                marginTop: 16,
              }}>
              {claiming ? (
                <ActivityIndicator color={c.accentForeground} />
              ) : (
                <Text
                  style={{
                    color: c.accentForeground,
                    fontFamily: TYPO.heading.bold,
                    fontSize: 16,
                  }}>
                  Submit Claim
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {/* Can't find gym */}
        {query.length > 1 && results.length === 0 && !searching && !selectedGym && (
          <View style={{ padding: 16, alignItems: 'center' }}>
            <Text style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center' }}>
              Don't see your gym? Email us at{' '}
              <Text style={{ color: c.accent }}>support@douevenlift.com</Text>
              {' '}and we'll add it.
            </Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
